import React, { useRef } from 'react'
import { jsPDF } from 'jspdf'
import { toPng } from 'html-to-image'
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { useTranslation } from 'react-i18next'

export default function VatInvoice ({
  reports = { reports: [], payer_name: '' }
}) {
  const { t, i18n } = useTranslation()
  const currentDate = new Date().toDateString()
  const currentTime = new Date().toTimeString().substring(0, 5)
  const invoiceRef = useRef(null)

  const date = new Date()
  const options = { year: 'numeric', month: 'long' }
  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date)

  const totalGross = reports.reports
    ?.reduce((acc, report) => acc + report.total_gross, 0)
    .toFixed(2)
  const totalNet = reports.reports
    ?.reduce((acc, report) => acc + report.total_net, 0)
    .toFixed(2)
  const totalVat = reports.reports
    ?.reduce((acc, report) => acc + report.total_net_vat, 0)
    .toFixed(2)
  const totalDiscount = reports.reports
    ?.reduce((acc, report) => acc + report.total_discount, 0)
    .toFixed(2)
  const totalPatientShare = reports.reports
    ?.reduce((acc, report) => acc + report.total_patient_share, 0)
    .toFixed(2)
  const totalTaxable = reports.reports
    ?.reduce((acc, report) => acc + report.total_taxable, 0)
    .toFixed(2)
  const totalNonTaxable = reports.reports
    ?.reduce((acc, report) => acc + report.total_non_taxable, 0)
    .toFixed(2)

  const handleDownloadPDF = async () => {
    const element = invoiceRef.current
    if (!element) return

    try {
      const dataUrl = await toPng(element, { quality: 0.95 })
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })

      const imgProps = pdf.getImageProperties(dataUrl)
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width

      pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight)
      pdf.save('vat_invoice.pdf')
    } catch (error) {
      console.error(t('Error generating PDF:'), error)
    }
  }

  return (
    <>
      <div className='flex justify-end my-6'>
        <Button
          variant='contained'
          color='primary'
          onClick={handleDownloadPDF}
          sx={{ mr: 2 }}
        >
          {t('Download PDF')}
        </Button>
      </div>
      <div className='p-8' ref={invoiceRef}>
        {/* Header */}
        <div className='flex flex-col gap-3 justify-between items-center mb-8'>
          <div className='font-bold text-lg'>VAT INVOICE</div>
          <div className='font-bold text-lg '>فاتوره ضريبه القيمه المضافه</div>
        </div>

        {/* Invoice Info */}
        <div className='grid mb-4'>
          <div className='flex flex-col gap-3 p-3'>
            <div className='flex justify-between'>
              <div className='font-medium text-base '>Invoice No :</div>
              <div className='text-base'></div>
              <div className='text-base font-bold'>رقم الفاتوره</div>
            </div>
            <div>
              <div className='flex justify-between'>
                <div className='font-medium text-base '>Invoice Date :</div>
                <div className='text-base font-bold'>
                  {currentDate} {currentTime}
                </div>
                <div className='text-base font-bold'>تاريخ إصدار الفاتوره</div>
              </div>
            </div>
          </div>
          {/* Tax Number & Facility Info */}
          <div className='flex flex-col gap-3 border-4 border-black p-3'>
            <div className='flex justify-between'>
              <div className='font-medium text-base '>Provider VAT :</div>
              <div className='text-base font-bold'></div>
              <div className='text-base font-bold'> الرقم الضريبي</div>
            </div>

            <div className='flex justify-between'>
              <div className='font-medium text-base '>Provider Name :</div>
              <div className='text-base font-bold'></div>
              <div className='text-base font-bold'>اسم المرفق الطبي</div>
            </div>

            <div className='flex justify-between'>
              <div className='font-medium text-base '>Provider Code :</div>
              <div className='text-base font-bold'></div>
              <div className='text-base font-bold'>رمز المرفق الطبي</div>
            </div>

            <div className='flex justify-between'>
              <div className='font-medium text-base '>Provider Address :</div>
              <div className='text-base font-bold'></div>
              <div className='text-base font-bold'>العنوان</div>
            </div>
          </div>

          <div className='flex flex-col gap-3 border-4 border-black p-3 mt-4'>
            <div className='flex justify-between'>
              <div className='font-medium text-base '>Client Name :</div>
              <div className='text-base font-bold'>{reports.payer_name}</div>
              <div className='text-base font-bold'>اسم الشركه</div>
            </div>

            <div className='flex justify-between'>
              <div className='font-medium text-base '>VAT No. :</div>
              <div className='text-base font-bold'></div>
              <div className='text-base font-bold'>الرقم الضريبي</div>
            </div>

            <div className='flex justify-between'>
              <div className='font-medium text-base '>Address :</div>
              <div className='text-base font-bold'></div>
              <div className='text-base font-bold'>العنوان</div>
            </div>
          </div>

          <div className='flex flex-col gap-3 border-4 border-black p-3 mt-4'>
            <div className='flex justify-between'>
              <div className='font-medium text-base '>Date Of Supply :</div>
              <div className='text-base font-bold'>{formattedDate}</div>
              <div className='text-base font-bold'>فتره التوريد</div>
            </div>

            <div className='flex justify-between'>
              <div className='font-medium text-base '>Bordereuax Date :</div>
              <div className='text-base font-bold'>{formattedDate}</div>
              <div className='text-base font-bold'>تاريخ الملحق</div>
            </div>
          </div>
        </div>

        <TableContainer component={Paper} className='border-2 border-black'>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className='!text-sm !font-bold border-2 !border-black'>
                  Details
                </TableCell>
                <TableCell
                  align='right'
                  className='!text-sm !font-bold border-2 !border-black'
                >
                  Amount / المبلغ
                </TableCell>
                <TableCell
                  align='right'
                  className='!text-sm !font-bold border-2 !border-black'
                >
                  التفاصيل
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  Claim Gross Amount
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {totalGross}
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  إجمالي المطالبه
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  Total Discount
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {totalDiscount}
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  إجمالي الخصومات
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  Total Deduct Amount
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {0}
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  إجمالي مبالغ التحمل
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  Patient Share Amount
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {totalPatientShare}
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  مبلغ مشاركة المريض
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  Total Net Amount
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {totalNet}
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  صافي المبلغ بعد الخصم والتحمل
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  Taxable Amount
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {totalTaxable}
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  المبلغ الخاضع للضريبة
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  Non-Taxable Amount
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {totalNonTaxable}
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  المبلغ غير الخاضع للضريبة
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  VAT % Applied
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {15}%
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  نسبة الضريبة المضافة
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  VAT Amount
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {totalVat}
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  قيمة الضريبة المضافة
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-2 !border-black !font-bold'>
                  Total Amount with VAT
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  {(parseFloat(totalNet) + parseFloat(totalVat)).toFixed(2)}
                </TableCell>
                <TableCell
                  align='right'
                  className='border-2 !border-black !font-bold'
                >
                  إجمالي المبلغ شامل الضريبة المضافة
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer */}
        <div className='grid grid-cols-3 mt-8'>
          <div></div>
          <div></div>

          <div className='border-t-4 border-black p-1.5'>
            <div className='text-base font-bold'>
              AUTHORIZED SIGNATORY & Stamp
            </div>
            <div className='text-xl fold-bold text-right'>
              توقيع المفوض والختم
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
